import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Checkbox,
  Collapse,
  Tag,
  Typography,
  Divider,
  Alert,
  message,
  Spin,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ListPageHeader from '@shared/ListPageHeader';
import { webhookService } from '@services/admin/webhookService';

const { Text, Paragraph } = Typography;

// Event categories and their events (matches backend Webhook::AVAILABLE_EVENTS)
const EVENT_GROUPS = {
  order: [
    'order.created',
    'order.updated',
    'order.cancelled',
    'order.shipped',
    'order.completed',
    'order.on_hold',
  ],
  inbound: [
    'inbound.created',
    'inbound.completed',
    'inbound.cancelled',
  ],
  inventory: [
    'inventory.updated',
    'inventory.low_stock',
  ],
  shipment: [
    'shipment.created',
    'shipment.shipped',
  ],
  return_order: [
    'return_order.created',
    'return_order.completed',
  ],
  product: [
    'product.created',
    'product.updated',
  ],
};

const ALL_EVENTS = Object.values(EVENT_GROUPS).flat();

export default function AdminWebhookEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const integrationId = location.state?.integrationId || null;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);

  useEffect(() => {
    if (!isNew) {
      fetchWebhook();
    }
  }, [id]);

  const fetchWebhook = async () => {
    try {
      setLoading(true);
      const data = await webhookService.get(id);
      if (data.success && data.data) {
        form.setFieldsValue({
          name: data.data.name,
          url: data.data.url,
          description: data.data.description,
        });
        setSelectedEvents(data.data.events || []);
      }
    } catch (error) {
      message.error(t('shared.webhooks.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  // pre-flight 驗證以 __aborted sentinel 統一處理（避免兩個 handler 重複驗證程式碼）
  const submitValues = async (values) => {
    if (selectedEvents.length === 0) {
      message.warning(t('shared.webhooks.form.events_hint'));
      return { __aborted: true };
    }
    const payload = {
      ...values,
      events: selectedEvents,
      ...(integrationId && { integration_id: integrationId }),
    };
    return isNew
      ? await webhookService.create(payload)
      : await webhookService.update(id, payload);
  };

  // 主要提交：成功後導向 show 頁
  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const data = await submitValues(values);
      if (data.__aborted) return;

      if (data.success) {
        message.success(
          isNew
            ? t('shared.webhooks.messages.create_success')
            : t('shared.webhooks.messages.update_success')
        );
        // 來源是 Integration 的 webhook 子頁時，導回該 integration 的 show 頁
        // 否則導向該 webhook 自身的 show 頁
        if (integrationId) {
          navigate(`/integrations/${integrationId}`, { replace: true });
        } else {
          const recordId = data.data?.id || id;
          navigate(`/webhooks/${recordId}`, { replace: true });
        }
      } else {
        message.error(data.message || t('shared.webhooks.error.create_failed'));
      }
    } catch (error) {
      message.error(
        isNew
          ? t('shared.webhooks.error.create_failed')
          : t('shared.webhooks.error.update_failed')
      );
    } finally {
      setSaving(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const data = await submitValues(values);
        if (data.__aborted) return;

        if (data.success) {
          message.success(t('shared.webhooks.messages.create_success'));
          // 重置 antd form + selectedEvents useState（不在 form 內）
          form.resetFields();
          setSelectedEvents([]);
        } else {
          message.error(data.message || t('shared.webhooks.error.create_failed'));
        }
      } catch (error) {
        message.error(t('shared.webhooks.error.create_failed'));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  const handleSelectAll = () => {
    setSelectedEvents(ALL_EVENTS);
  };

  const handleDeselectAll = () => {
    setSelectedEvents([]);
  };

  const handleCategoryToggle = (category, events) => {
    const allSelected = events.every((e) => selectedEvents.includes(e));
    if (allSelected) {
      setSelectedEvents(selectedEvents.filter((e) => !events.includes(e)));
    } else {
      setSelectedEvents([...new Set([...selectedEvents, ...events])]);
    }
  };

  const handleEventToggle = (event) => {
    if (selectedEvents.includes(event)) {
      setSelectedEvents(selectedEvents.filter((e) => e !== event));
    } else {
      setSelectedEvents([...selectedEvents, event]);
    }
  };

  const collapseItems = Object.entries(EVENT_GROUPS).map(([category, events]) => {
    const selectedCount = events.filter((e) => selectedEvents.includes(e)).length;
    const allSelected = selectedCount === events.length;

    return {
      key: category,
      label: (
        <Space>
          <Checkbox
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            onChange={() => handleCategoryToggle(category, events)}
            onClick={(e) => e.stopPropagation()}
          />
          <Text strong>
            {t(`shared.webhooks.event_categories.${category}`)}
          </Text>
          <Tag>{selectedCount}/{events.length}</Tag>
        </Space>
      ),
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          {events.map((event) => (
            <Checkbox
              key={event}
              checked={selectedEvents.includes(event)}
              onChange={() => handleEventToggle(event)}
            >
              <Space>
                <Text code>{event}</Text>
                <Text type="secondary">
                  {t(`shared.webhooks.events.${event}`, event)}
                </Text>
              </Space>
            </Checkbox>
          ))}
        </Space>
      ),
    };
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  const backPath = integrationId ? `/integrations/${integrationId}` : '/webhooks';

  return (
    <div>
      <ListPageHeader basePath="/admin" items={['developer_tools', integrationId ? 'developer_tools.integrations' : 'developer_tools.webhooks']} />
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ maxWidth: 800 }}
        >
          <Form.Item
            name="name"
            label={t('shared.webhooks.fields.name')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input placeholder={t('shared.webhooks.placeholder.name')} />
          </Form.Item>

          <Form.Item
            name="url"
            label={t('shared.webhooks.fields.url')}
            rules={[
              { required: true, message: t('common.validation.required') },
              { type: 'url', message: t('shared.webhooks.form.url_hint') },
            ]}
            extra={t('shared.webhooks.form.url_hint')}
          >
            <Input placeholder={t('shared.webhooks.placeholder.url')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('shared.webhooks.fields.description')}
          >
            <Input.TextArea
              rows={2}
              placeholder={t('shared.webhooks.placeholder.description')}
            />
          </Form.Item>

          <Divider />

          {/* Events Selection */}
          <div style={{ marginBottom: 24 }}>
            <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 16 }}>
                {t('shared.webhooks.fields.events')}
              </Text>
              <Space>
                <Button size="small" onClick={handleSelectAll}>
                  {t('shared.webhooks.form.select_all')}
                </Button>
                <Button size="small" onClick={handleDeselectAll}>
                  {t('shared.webhooks.form.deselect_all')}
                </Button>
              </Space>
            </Space>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {t('shared.webhooks.form.events_hint')}
            </Paragraph>

            {selectedEvents.length === 0 && (
              <Alert
                message={t('shared.webhooks.form.events_hint')}
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}

            <Collapse
              items={collapseItems}
              defaultActiveKey={Object.keys(EVENT_GROUPS)}
              bordered={false}
            />

            <div style={{ marginTop: 12 }}>
              <Text type="secondary">
                {selectedEvents.length} / {ALL_EVENTS.length} {t('shared.webhooks.fields.events').toLowerCase()}
              </Text>
            </div>
          </div>

          <Divider />

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                disabled={savingAndAdding}
              >
                {isNew ? t('common.button.create') : t('common.button.save')}
              </Button>
              {isNew && (
                <Button
                  loading={savingAndAdding}
                  disabled={saving}
                  onClick={handleSaveAndAddAnother}
                >
                  {t('common.button.save_and_add_another')}
                </Button>
              )}
              <Button onClick={() => navigate(backPath)}>
                {t('common.button.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
